import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStorageKeys } from '@app/core/models/configuration/sessionstorage-keys.model';
import { TableColumnsConfigHelper } from '@app/core/models/configuration/table-columns-config-helper';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import {
  FaultedInvoiceModel,
  FaultedInvoiceType,
} from '@app/core/models/invoices/faulted-invoice.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { AdminService } from '@app/core/services/admin.service';
import { FileDownloadService } from '@app/core/services/filedownload.service';
import { InvoiceService } from '@app/core/services/invoice.service';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ColumnDefinitionModel } from '@app/shared/models/column-definition.model';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import {
  TableActionIcon,
  TableIndication,
  TableMassAction,
  TableRowAction,
} from '@app/shared/models/table-action-type.model';
import { InvoiceStatePipe } from '@app/shared/pipes/invoice-state.pipe';
import { FaultedInvoiceDetailDetailComponent } from './detail/faulted-invoice-detail.component';
import { AttachmentType } from '@app/core/models/attachment-type.model';

@Component({
  selector: 'app-faulted-invoices',
  templateUrl: './faulted-invoices.component.html',
  styleUrls: ['./faulted-invoices.component.scss'],
})
export class FaultedInvoicesComponent implements OnInit, OnDestroy {
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  allInvoices: FaultedInvoiceModel[] = [];
  invoices: FaultedInvoiceModel[] = [];
  tableIndications: TableIndication[] = [];
  tableActions: TableRowAction[] = [];
  tableMassActions: TableMassAction[] = [];
  configKey: string;
  columnsDefinition: ColumnDefinitionModel[] = [];
  exportFileName: string;
  selectedIds: string[] = [];
  isAllSelected: boolean = false;
  fromDate: string;
  toDate: string;

  public onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
  public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  panelOpenState: boolean = true;
  title: string;

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private invoiceService: InvoiceService,
    private sessionStorageService: SessionStorageService,
    private fileDownloadService: FileDownloadService,
    private invoiceStatePipe: InvoiceStatePipe
  ) {
    this.title = 'FACTURES EN ANOMALIE';
  }

  ngOnInit(): void {
    this.columnsDefinition =
      new TableColumnsConfigHelper().getColumnsDefinition(
        TableColumnsConfigKey.FAULTEDINVOICES
      );

    this.route.data.subscribe((data: any) => {
      this.allInvoices = this.invoices = data.invoices;
      this.configKey = data.configKey;
      this.exportFileName = data.exportFileName;
      this.restoreFilters();
    });

    this.initTableActions();
  }

  restoreFilters() {
    const dateFilters = this.sessionStorageService.get<any>(
      SessionStorageKeys.INVOICE_WRONG_STATE
    );
    if (!_.isNil(dateFilters)) {
      if (!_.isNil(dateFilters.fromDate)) {
        this.fromDate = dateFilters.fromDate;
      }
      if (!_.isNil(dateFilters.toDate)) {
        this.toDate = dateFilters.toDate;
      }
    }
    this.onFilters();
  }

  initTableActions() {
    this.tableActions.push(
      new TableRowAction(TableActionIcon.ZOOM, 'Voir le détail', this.onZoom)
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.DOWNLOAD,
        'Télécharger le document',
        this.onDownload,
        this.ensureIsNotCreationFailure
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.DOWNLOAD,
        'Télécharger le document',
        this.onDownloadCreationFailure,
        this.ensureIsCreationFailure
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.REPAIR,
        'Corriger la facture',
        this.onFixInvoiceWrongState,
        this.ensureIsWrongState
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.REPAIR,
        'Réinitialiser la facture',
        this.onFixInvoiceStuck,
        this.ensureIsStuck
      )
    );
    this.tableActions.push(
      new TableRowAction(
        TableActionIcon.REPAIR,
        "Relancer l'import",
        this.onFixInvoiceCreationFailure,
        this.ensureIsCreationFailure
      )
    );
    this.tableMassActions.push(
      new TableMassAction(
        TableActionIcon.REPAIR,
        'Corriger les factures',
        this.onFixInvoices
      )
    );
  }

  onSubmit(): void {
    this.onFilters();
    this.sessionStorageService.store(SessionStorageKeys.INVOICE_WRONG_STATE, {
      fromDate: this.fromDate,
      toDate: this.toDate,
    });
  }

  onFilters(): void {
    this.invoices = this.allInvoices.filter(
      (invoice: FaultedInvoiceModel) =>
        (_.isNil(this.fromDate) ||
          moment(invoice.IssuedAt).isSameOrAfter(moment(this.fromDate))) &&
        (_.isNil(this.toDate) ||
          moment(invoice.IssuedAt).isBefore(moment(this.toDate).add(1, 'day')))
    );
  }

  onZoom = (faultedInvoice: FaultedInvoiceModel) => {
    if (faultedInvoice.Type === FaultedInvoiceType.CREATIONFAILURE) {
      const dialogModal = new ModalHelper().getFaultedInvoiceModalConfig(
        faultedInvoice
      );
      const dialogRef = this.dialog.open(
        FaultedInvoiceDetailDetailComponent,
        dialogModal
      );
      return dialogRef.afterClosed();
    }

    this.router.navigate([
      `invoices/${InvoiceViewType.SEARCH}/detail`,
      faultedInvoice.Id,
    ]);
  };

  onDownload = (faultedInvoice: FaultedInvoiceModel) => {
    const attachment = faultedInvoice.Attachments.find(
      (a) => a.TypeId === AttachmentType.INVOICE
    );
    this.invoiceService
      .getAttachment(faultedInvoice.Id, attachment.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (blobData: Blob) => {
          this.fileDownloadService.downloadBlob(blobData, attachment.Name);
        },
        (_) => {
          const modalErreurConfig = new ModalHelper().getAlertConfig(
            `Une erreur est survenue lors du téléchargement.`
          );
          this.dialog.open(ModalComponent, modalErreurConfig);
        }
      );
  };

  onDownloadCreationFailure = (faultedInvoice: FaultedInvoiceModel) => {
    this.adminService
      .getAttachmentContent(faultedInvoice.Id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (blobData: Blob) => {
          this.fileDownloadService.downloadBlob(
            blobData,
            faultedInvoice.DocumentFileName
          );
        },
        (_) => {
          const modalErreurConfig = new ModalHelper().getAlertConfig(
            `Une erreur est survenue lors du téléchargement.`
          );
          this.dialog.open(ModalComponent, modalErreurConfig);
        }
      );
  };

  dateChangeSearch(result: any) {
    this.dateChange(result.type, result.selectedDate);
  }

  dateChange(type: string, selectedDate: any): void {
    var formated = !_.isNil(selectedDate)
      ? moment(selectedDate).format('YYYY-MM-DD')
      : null;

    switch (type) {
      case 'FromDate':
        this.fromDate = formated;
        break;
      case 'ToDate':
        this.toDate = formated;
        break;
    }

    this.onSubmit();
  }

  clearSearchFilter(): void {
    this.onClearFilters();
    this.onFiltersCleared.next();
  }

  onClearFilters = (): void => {
    this.fromDate = null;
    this.toDate = null;
    this.onSubmit();
  };

  onRowSelectionChange = (tableRowsSelection: string[]) => {
    this.selectedIds = tableRowsSelection;
  };

  onAllRowSelectionChange = (isChecked: boolean) => {
    this.isAllSelected = isChecked;
  };

  onFixInvoiceWrongState = (invoice: FaultedInvoiceModel): void => {
    const message = `Êtes-vous sûr de vouloir changer le statut de la facture <b>${
      invoice.Number
    }</b> en <b>${this.invoiceStatePipe.transform(
      invoice.ExpectedState
    )}</b> ?`;
    this.fixInvoice(invoice, message);
  };

  onFixInvoiceStuck = (invoice: FaultedInvoiceModel): void => {
    const message = `Êtes-vous sûr de vouloir changer réinitialiser le traitement de la facture <b>${invoice.Number}</b> ?`;
    this.fixInvoice(invoice, message);
  };

  onFixInvoiceCreationFailure = (invoice: FaultedInvoiceModel): void => {
    const message = `Êtes-vous sûr de vouloir changer relancer l'import de la facture <b>${invoice.Number}</b> ?`;
    this.fixInvoice(invoice, message);
  };

  fixInvoice = (invoice: FaultedInvoiceModel, message: string): void => {
    const modalConfig = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.onFixInvoicesConfirmed([invoice.Id]);
        }
      });
  };

  onFixInvoices = (invoicesId: string[]): void => {
    const message = `Êtes-vous sûr de vouloir réparer ces factures ?`;
    const modalConfig = new ModalHelper().getConfirmationConfig(message);
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.onFixInvoicesConfirmed(invoicesId);
        }
      });
  };

  onFixInvoicesConfirmed = (invoicesId: string[]): void => {
    this.adminService
      .fixFaultedInvoices(invoicesId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        this.allInvoices = this.allInvoices.filter(
          (invoice) => !invoicesId.includes(invoice.Id)
        );
        this.onFilters();
      });
  };

  ensureIsCreationFailure = (faultedInvoice: FaultedInvoiceModel) => {
    return faultedInvoice.Type === FaultedInvoiceType.CREATIONFAILURE;
  };

  ensureIsStuck = (faultedInvoice: FaultedInvoiceModel) => {
    return faultedInvoice.Type === FaultedInvoiceType.STUCK;
  };

  ensureIsWrongState = (faultedInvoice: FaultedInvoiceModel) => {
    return faultedInvoice.Type === FaultedInvoiceType.WRONGSTATE;
  };

  ensureIsNotCreationFailure = (faultedInvoice: FaultedInvoiceModel) => {
    return faultedInvoice.Type !== FaultedInvoiceType.CREATIONFAILURE;
  };

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }

  exportAsExcel(): void {
    this.onExportExcelCaller.next();
  }

  onShowFilter(panelvalue: boolean) {
    this.panelOpenState = panelvalue;
  }
}
